import { createRoot } from 'react-dom/client';

// Types
import { AppStore } from 'types/RootState.types';

// Config
import { URL_FEATURES } from '@constants/endpointConstants';
import routeConstants from '@constants/routeConstants';
import mobileAppsActions from '@redux/actions/mobileApps/mobileAppsActions';
import configureStore from '@redux/configureStore';
import api from '@utils/api/api';
import unionWithDevFeatures from '@utils/features/feature-utils';
import createBrowserHistory from '@utils/history/history';
import { initLogging } from '@utils/logging/logging-utils';

// Components
import Root from './Root';

function onRender(store: AppStore) {
  store.dispatch(initLogging());
}

export function initStore(initialState = {}) {
  const history = createBrowserHistory();
  const { store, persistor } = configureStore(history, initialState);

  return { history, store, persistor };
}

export function init(initialState = {}) {
  const { history, store, persistor } = initStore(initialState);

  // Set up Apps actions
  window.appsActions = mobileAppsActions(store);

  const container = document.getElementById('page-root');
  if (container) {
    const reactDOM = createRoot(container);

    const root = <Root history={history} persistor={persistor} store={store} />;
    reactDOM.render(root);

    if (module.hot) {
      module.hot.accept('./Root', () => {
        const NewRoot = require('./Root').default; // eslint-disable-line global-require
        const root = <NewRoot history={history} persistor={persistor} store={store} />;
        reactDOM.render(root);
      });
    }

    setTimeout(() => onRender(store), 0);
  }
}

const getFeatures = () =>
  api({
    path: URL_FEATURES,
    config: { method: 'GET' },
  })
    .then((response) => {
      const bffFeatures = response?.features ?? [];
      return unionWithDevFeatures(bffFeatures);
    })
    .catch(() => []);

const skipGetFeaturesCallOnPaths = [
  routeConstants.LOGIN,
  routeConstants.WEB_HANDOVER,
  routeConstants.APPS_GUEST_HANDOVER,
  routeConstants.APPS_AUTHENTICATED_HANDOVER,
  routeConstants.CALLBACK_LOGIN_AUTHENTICATED,
  routeConstants.CALLBACK_LOGIN_GUEST,
  routeConstants.CALLBACK_ORDER_CLAIM,
];

export default async function initApp() {
  window.timeToReactLoaded = Date.now();

  const features = skipGetFeaturesCallOnPaths.includes(window.location.pathname) ? [] : await getFeatures();

  return init({
    config: {
      features,
    },
  });
}

initApp();
